// @ts-strict-ignore
import { CardBlocks, CardCollectionAgendaTemplate } from '@coast/schemas';
import { debounce, first, groupBy, last, sortBy } from 'lodash';
import * as ms from 'milliseconds';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollViewProps } from 'react-native';
import {
  CardPaginationDateFilter,
  CardPaginationQuery,
  CardPaginationQueryStateWithCards,
  useCardPaginationActions,
  useCardPaginationState,
} from '@shared/api/providers/cardPagination';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { calculateCardHeight } from '@shared/components/blocks/render';
import { extractAllFields } from '@shared/components/blocks/utils';
import { fontTextRegular } from '@shared/components/typography';
import { AgendaItem, DateString, dateString, getData } from '@shared/scenes/cards/agenda/utils';
import { useCardPaginationFilters, useResolveCardDefinition } from '@shared/scenes/cards/hooks';
import { ColorThemeContext, Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { useChannelUsers } from '@shared/util/hooks';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { PrintModeContext } from '@shared/util/print/PrintContext';

interface UseAgendaDataResponse {
  data: AgendaItem[];
  loading: boolean;
  rangeStartDate: string;
  rangeEndDate: string;
  loadNext(): Promise<AgendaItem[]>;
  loadPrev(): Promise<AgendaItem[]>;
}

function minDate(...dateStrings: (string | undefined)[]): moment.Moment {
  const dates = dateStrings.filter(d => !!d).map(d => moment(d));
  return moment.min(dates);
}

function maxDate(...dateStrings: string[]): moment.Moment {
  const dates = dateStrings.filter(d => !!d).map(d => moment(d));
  return moment.max(dates);
}

function firstCard(
  prevSection: CardPaginationQueryStateWithCards | undefined,
  nextSection: CardPaginationQueryStateWithCards | undefined,
): API.CardFragment | undefined {
  return last(prevSection?.cards) || first(nextSection?.cards);
}

function lastCard(
  prevSection: CardPaginationQueryStateWithCards | undefined,
  nextSection: CardPaginationQueryStateWithCards | undefined,
): API.CardFragment | undefined {
  return last(nextSection?.cards) || first(prevSection?.cards);
}

export function useAgendaData(
  startDate: string,
  template: CardCollectionAgendaTemplate,
  channel?: API.ChannelFragment,
): UseAgendaDataResponse {
  const { getState } = useCardPaginationState();
  const { fetchMore } = useCardPaginationActions();
  const { users } = useChannelUsers({ channelId: channel?.id });
  const filters = useCardPaginationFilters(template, users);

  const prevDateFilter = useMemo(
    (): CardPaginationDateFilter => ({
      type: 'startDate',
      // Prevent overlap if a card's startDate === hook startDate
      before: moment(startDate).subtract(1, 'millisecond').toISOString(),
    }),
    [startDate],
  );
  const nextDateFilter = useMemo(
    (): CardPaginationDateFilter => ({ type: 'startDate', after: startDate }),
    [startDate],
  );

  const baseQuery = useMemo<CardPaginationQuery>(
    () => ({
      channelId: channel?.id,
      statuses: ['ACTIVE', 'PENDING'],
      order: [],
    }),
    [channel?.id],
  );
  const prevQuery = useMemo<CardPaginationQuery>(
    (): CardPaginationQuery => ({
      ...baseQuery,
      filters: [...(filters || []), prevDateFilter],
      order: [{ orderBy: 'startDate', orderDirection: 'DESC' }],
    }),
    [baseQuery, filters, prevDateFilter],
  );
  const nextQuery = useMemo<CardPaginationQuery>(
    (): CardPaginationQuery => ({
      ...baseQuery,
      filters: [...(filters || []), nextDateFilter],
      order: [{ orderBy: 'startDate', orderDirection: 'ASC' }],
    }),
    [baseQuery, filters, nextDateFilter],
  );

  const nextState = useMemo(() => getState(nextQuery), [getState, nextQuery]);
  const prevState = useMemo(() => getState(prevQuery), [getState, prevQuery]);

  const [rangeStartDateExtentOverride, setRangeStartDateExtentOverride] = useState<string>();
  const [rangeEndDateExtentOverride, setRangeEndDateExtentOverride] = useState<string>();

  useEffect(() => {
    setRangeStartDateExtentOverride(undefined);
    setRangeEndDateExtentOverride(undefined);
  }, [startDate]);

  const calculateRangeStartDate = useCallback(
    (
      _prevState: CardPaginationQueryStateWithCards = prevState,
      _nextState: CardPaginationQueryStateWithCards = nextState,
    ) => {
      return minDate(
        rangeStartDateExtentOverride,
        firstCard(_prevState, _nextState)?.startDate ?? undefined,
        startDate,
      )
        .startOf('day')
        .toISOString();
    },
    [prevState, nextState, rangeStartDateExtentOverride, startDate],
  );
  const calculateRangeEndDate = useCallback(
    (
      _prevSection: CardPaginationQueryStateWithCards = prevState,
      _nextSection: CardPaginationQueryStateWithCards = nextState,
    ) => {
      return maxDate(
        rangeEndDateExtentOverride,
        lastCard(_prevSection, _nextSection)?.startDate,
        startDate,
      )
        .endOf('day')
        .toISOString();
    },
    [prevState, nextState, rangeEndDateExtentOverride, startDate],
  );

  const rangeStartDate = useMemo(calculateRangeStartDate, [calculateRangeStartDate]);
  const rangeEndDate = useMemo(calculateRangeEndDate, [calculateRangeEndDate]);

  const loading = useMemo(() => !(nextState?.loaded && prevState?.loaded), [nextState, prevState]);

  useEffect(() => {
    if (!!channel?.id) {
      if (!nextState) {
        fetchMore(nextQuery);
      }
      if (!prevState) {
        fetchMore(prevQuery);
      }
    }
  }, [channel, nextState, prevState, fetchMore, nextQuery, prevQuery]);

  const calcCardHeight = useCalculateCardHeight(template.cards[0].cardBlocks);
  const loadData = useCallback(
    (
      prevSection: CardPaginationQueryStateWithCards | undefined,
      nextSection: CardPaginationQueryStateWithCards | undefined,
      rStartDate: string,
      rEndDate: string,
    ): AgendaItem[] => {
      const prevCards = prevSection?.cards || [];
      const nextCards = nextSection?.cards || [];
      const allCards = [...prevCards, ...nextCards];
      const sortedCards = sortBy(allCards, 'startDate', 'createdAt');
      const cardsByDate = groupBy(sortedCards, card => dateString(card.startDate));
      return getData(cardsByDate, [rStartDate, rEndDate], calcCardHeight);
    },
    [calcCardHeight],
  );

  const [data, setData] = useState<AgendaItem[]>(() =>
    loadData(prevState, nextState, rangeStartDate, rangeEndDate),
  );

  const expandRangeStart = useCallback((): AgendaItem[] => {
    const newRangeStartDate = moment(rangeStartDate).subtract(14, 'days').toISOString();
    const newData = loadData(prevState, nextState, newRangeStartDate, rangeEndDate);
    setRangeStartDateExtentOverride(newRangeStartDate);
    setData(newData);
    return newData;
  }, [rangeStartDate, loadData, prevState, nextState, rangeEndDate]);

  const expandRangeEnd = useCallback((): AgendaItem[] => {
    const newRangeEndDate = moment(rangeEndDate).add(14, 'days').toISOString();
    const newData = loadData(prevState, nextState, rangeStartDate, newRangeEndDate);
    setRangeEndDateExtentOverride(newRangeEndDate);
    setData(newData);
    return newData;
  }, [rangeEndDate, loadData, prevState, nextState, rangeStartDate]);

  useEffect(() => {
    if (!prevState || !nextState) {
      return;
    }

    const newData = loadData(prevState, nextState, rangeStartDate, rangeEndDate);
    setData(newData);

    if (prevState.loaded && !prevState.hasMore && !rangeStartDateExtentOverride) {
      expandRangeStart();
    }

    if (nextState.loaded && !nextState.hasMore && !rangeEndDateExtentOverride) {
      expandRangeEnd();
    }
  }, [
    loadData,
    prevState,
    nextState,
    rangeStartDate,
    rangeEndDate,
    expandRangeStart,
    expandRangeEnd,
    rangeStartDateExtentOverride,
    rangeEndDateExtentOverride,
  ]);

  const loadPrev = useCallback(async (): Promise<AgendaItem[]> => {
    if (prevState?.hasMore) {
      const newPrevSection = await fetchMore(prevQuery);
      const newRangeStartDate = calculateRangeStartDate(newPrevSection, nextState);
      const newData = loadData(newPrevSection, nextState, newRangeStartDate, rangeEndDate);
      setData(newData);
      return newData;
    } else {
      return expandRangeStart();
    }
  }, [
    prevState,
    fetchMore,
    prevQuery,
    calculateRangeStartDate,
    nextState,
    loadData,
    rangeEndDate,
    expandRangeStart,
  ]);

  const loadNext = useCallback(async (): Promise<AgendaItem[]> => {
    if (nextState?.hasMore) {
      const newNextSection = await fetchMore(nextQuery);
      const newRangeEndDate = calculateRangeEndDate(prevState, newNextSection);
      const newData = loadData(prevState, newNextSection, rangeStartDate, newRangeEndDate);
      setData(newData);
      return newData;
    } else {
      return expandRangeEnd();
    }
  }, [
    nextState,
    fetchMore,
    nextQuery,
    calculateRangeEndDate,
    prevState,
    loadData,
    rangeStartDate,
    expandRangeEnd,
  ]);

  return useMemo(
    () => ({ data, loading, rangeStartDate, rangeEndDate, loadNext, loadPrev }),
    [data, loading, rangeStartDate, rangeEndDate, loadNext, loadPrev],
  );
}

export type MarkedDates = {
  [key: string]: {
    dots?: { key: string; color: string; selectedDotColor: string }[];
    selected?: boolean;
    selectedColor?: string;
  };
};

export function useMarkedDates(selectedDate: DateString): MarkedDates {
  return useMemo(
    () => ({
      [selectedDate]: {
        selected: true,
        selectedColor: moment(selectedDate).isSame(moment(), 'day')
          ? Colors.v2.green
          : Colors.v2.purple,
      },
    }),
    [selectedDate],
  );
}

export function useCalendarTheme(): { [key: string]: any } {
  const colors = useContext(ColorThemeContext);
  return useRef({
    todayTextColor: colors.accent,

    textDayHeaderFontSize: 16,
    textDayHeaderFontWeight: '600',
    textDayHeaderFontFamily: fontTextRegular.fontFamily,

    textDayFontSize: 17,
    textDayFontWeight: '600',
    textDayFontFamily: fontTextRegular.fontFamily,
    dayTextColor: colors.foregroundActive,
    textDisabledColor: colors.foregroundInactive,
  }).current;
}

export function useToday(): string {
  const [today, setToday] = useState<string>(dateString());

  useEffect(() => {
    // update the now marker every 5 minutes
    const h = setInterval(() => setToday(dateString()), ms.minutes(5));
    return () => clearInterval(h);
  }, []);

  return useMemo<string>(() => today, [today]);
}

export function useNow(): string {
  const [now, setNow] = useState<string>(moment().toISOString());

  useEffect(() => {
    // update the now marker every 5 minutes
    const h = setInterval(() => setNow(moment().toISOString()), ms.minutes(5));
    return () => clearInterval(h);
  }, []);

  return useMemo<string>(() => now, [now]);
}

// https://github.com/necolas/react-native-web/issues/1021
export function useSimulatedMomentumScrollEnd(
  onMomentumScrollEnd: ScrollViewProps['onMomentumScrollEnd'],
  increment: number,
) {
  const onMomentumScrollEndDebounced = useMemo<ScrollViewProps['onMomentumScrollEnd']>(
    () => (onMomentumScrollEnd ? debounce(onMomentumScrollEnd, 100) : undefined),
    [onMomentumScrollEnd],
  );

  const onScroll = useCallback(
    event => {
      const x = event.nativeEvent.contentOffset.x;
      if (x % increment === 0) {
        onMomentumScrollEndDebounced?.(event);
      }
    },
    [onMomentumScrollEndDebounced, increment],
  );

  return onScroll;
}

export function useCalculateCardHeight(blocks: CardBlocks): (card: API.CardFragment) => number {
  const channelPermissions = useContext(ChannelPermissionsContext);
  const renderContext = useContext(CardRenderContext);
  const printing = useContext(PrintModeContext);
  const resolveCardDefinition = useResolveCardDefinition();
  return useCallback(
    (card: API.CardFragment) => {
      const cardDefinition = resolveCardDefinition(card.cardDefinitionId);
      const fields = extractAllFields(card, cardDefinition?.definition);
      return calculateCardHeight(
        blocks,
        card,
        fields,
        channelPermissions.canReadThread.enabled,
        channelPermissions.canCreateThread.enabled,
        channelPermissions.canUpdateCard.enabled,
        printing,
        renderContext,
      );
    },
    [
      blocks,
      channelPermissions.canCreateThread.enabled,
      channelPermissions.canReadThread.enabled,
      channelPermissions.canUpdateCard.enabled,
      printing,
      renderContext,
      resolveCardDefinition,
    ],
  );
}
